import React, { useState, useEffect, useCallback } from "react";
import axios from "axios";
import {
  Container,
  Row,
  Col,
  Accordion,
  Button,
  Spinner,
  Alert,
  Badge,
  Toast,
  ToastContainer,
  Modal,
  NavLink,
} from "react-bootstrap";
import "plyr-react/plyr.css";
import { FaCalendar, FaVideo, FaPlay } from "react-icons/fa";
import { Link, useNavigate } from "react-router-dom";
import liveclass from "../../../assets/images/liveclass.png";
import DOMPurify from "dompurify";

const LiveClass = () => {
  const [liveClasses, setLiveClasses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [activeKey, setActiveKey] = useState("0");
  const [now, setNow] = useState(new Date());
  const [showToast, setShowToast] = useState(false);
  const [toastMessage, setToastMessage] = useState("");
  const navigate = useNavigate();
  const token = localStorage.getItem("token");
  const user_id = localStorage.getItem("user_id");
  const [selectedClass, setSelectedClass] = useState(null);

  const [show, setShow] = useState(false);

  const handleClose = () => setShow(false);
  const handleShow = () => setShow(true);

  // Fetch live classes
  // const fetchLiveClasses = useCallback(async () => {
  //   try {
  //     setLoading(true);
  //     setError(null);
  //     const res = await axios.get(
  //       `${process.env.REACT_APP_BASE_URL}/live-class-list`
  //     );

  //     if (res.data.status === "1" && res.data.data) {
  //       setLiveClasses(res.data.data);
  //     } else {
  //       setError("No live classes available at the moment.");
  //     }
  //   } catch (err) {
  //     console.error("Error fetching live classes:", err);
  //     setError("Failed to load live classes. Please try again later.");
  //   } finally {
  //     setLoading(false);
  //   }
  // }, []);

  const fetchLiveClasses = useCallback(async () => {
    setLoading(true);
    try {
      const planRes = await fetch(
        `${process.env.REACT_APP_BASE_URL}/plan-package-list`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          },
          body: JSON.stringify({ id: user_id }),
        }
      );

      if (!planRes.ok) throw new Error("Failed to fetch plan");

      const planData = await planRes.json();

      const allCourseIds = planData.data.map((item) => item.course_id);
      const uniqueCourseIds = [...new Set(allCourseIds)];
      const liveRes = await axios.get(
        `${process.env.REACT_APP_BASE_URL}/live-class-list`
      );

      if (liveRes.data.status === "1" && liveRes.data.data) {
        const numericCourseIds = uniqueCourseIds.map((id) => Number(id));
        const filteredLiveClasses = liveRes.data.data.filter((liveClass) =>
          numericCourseIds.includes(liveClass.course_id)
        );

        setLiveClasses(filteredLiveClasses);
        setLoading(false);
      } else {
        setError("No live classes available at the moment.");
      }
    } catch (err) {
      console.error("Error fetching data:", err);
      setLoading(false);
      setError("No live classes available at the moment.");
    }
  }, []);

  useEffect(() => {
    fetchLiveClasses();
  }, [fetchLiveClasses]);

  // Update current time every 30 seconds
  useEffect(() => {
    const interval = setInterval(() => {
      setNow(new Date());
    }, 30000);

    return () => clearInterval(interval);
  }, []);

  const handleSubmit = async (id) => {
    try {
      await axios.post(`${process.env.REACT_APP_BASE_URL}/live-class-user`, {
        user_id: user_id,
        live_class_id: id,
      });
    } catch (error) {
      console.error("Error submitting live class registration:", error);
    }
  };

  const isTimeToJoin = (cls) => {
    const start = new Date(cls.start_time);
    const earlyJoinBuffer = 10; // minutes before start allowed to join
    return now >= new Date(start.getTime() - earlyJoinBuffer * 60000);
  };

  // Join Zoom meeting
  const handleJoinZoom = async (cls) => {
    if (!user_id) {
      setToastMessage("Please login to join the live class");
      setShowToast(true);
      navigate("/login");
      return;
    }
    const status = getClassStatus(cls);
    if (status.status !== "ongoing") {
      setToastMessage("This class is not currently live.");
      setShowToast(true);
      return;
    }
    try {
      await handleSubmit(cls.id);
      localStorage.setItem("meetingid", cls.meeting_id);
      localStorage.setItem("password", cls.password);
      navigate("/Live-meeting");
    } catch (error) {
      setToastMessage("Failed to join the meeting. Please try again.");
      setShowToast(true);
    }
  };
  const handleJoinModel = (cls) => {
    if (!user_id) {
      setToastMessage("Please login to join the live class");
      setShowToast(true);
      navigate("/login");
      return;
    }

    setSelectedClass(cls); // ✅ save selected class
    handleShow(); // open modal
  };

  const formatDateTime = (dateTimeString) => {
    try {
      return new Date(dateTimeString).toLocaleString();
    } catch (error) {
      return "Invalid date";
    }
  };

  const getClassStatus = (cls) => {
    try {
      const start = new Date(cls.start_time);
      const end = new Date(start.getTime() + cls.duration_minutes * 60000);

      if (now < start) {
        return { status: "scheduled", label: "Scheduled", variant: "warning" };
      } else if (now >= start && now < end) {
        return { status: "ongoing", label: "Ongoing", variant: "success" };
      } else {
        return {
          status: "completed",
          label: "Completed",
          variant: "secondary",
        };
      }
    } catch (error) {
      return { status: "error", label: "Error", variant: "danger" };
    }
  };

  const renderAccordionItems = () => {
    const paidClasses = liveClasses.filter((cls) => cls.type === "paid");
    // ✅ Sort so that ongoing classes come first, then scheduled, then completed

    if (paidClasses.length === 0) {
      return (
        <div className="text-center p-4">
          <FaVideo size={48} className="text-muted mb-3" />
          <p>No paid live classes available.</p>
        </div>
      );
    }
    const statusOrder = { ongoing: 0, scheduled: 1, completed: 2 };

    const sortedClasses = [...paidClasses].sort((a, b) => {
      const statusA = getClassStatus(a).status;
      const statusB = getClassStatus(b).status;
      return statusOrder[statusA] - statusOrder[statusB];
    });

    return sortedClasses.map((cls, idx) => {
      const status = getClassStatus(cls);

      return (
        <Accordion.Item eventKey={idx.toString()} key={cls.id || idx}>
          <Accordion.Header>
            <div className="accordian_course d-flex justify-content-between align-items-center w-100 me-3">
              <span className="fw-medium">{cls.title}</span>
              <Badge bg={status.variant} pill>
                {status.label}
              </Badge>
            </div>
          </Accordion.Header>
          <Accordion.Body>
            <p className="mb-2">
              <FaCalendar className="me-2" />
              {formatDateTime(cls.start_time)}
            </p>
            <p className="mb-2 font-bold">
              {"Duration of the class: " + cls.duration_minutes + " minutes"}
            </p>
            {cls.description && (
              <p
                className="mb-4p desc"
                dangerouslySetInnerHTML={{
                  __html: DOMPurify.sanitize(cls?.description || ""),
                }}
              />
            )}

            <div className="d-grid gap-2">
              {status.status === "scheduled" && (
                <Button variant="outline-primary" disabled>
                  <FaPlay className="me-2" />
                  Class Not Started Yet
                </Button>
              )}

              {status.status === "ongoing" &&
                (isTimeToJoin(cls) ? (
                  <>
                    <div className="d-flex gap-3 justify-content-between mt-1">
                      <button
                        onClick={() => handleJoinZoom(cls)}
                        className="educate-btn d-flex align-items-center justify-content-center w-100"
                      >
                        <span className="educate-btn__curve" />
                        <FaPlay className="me-2" />
                        Join Using Website
                      </button>

                      <button
                        onClick={() => handleJoinModel(cls)}
                        className="educate-btn bg-primary d-flex align-items-center justify-content-center w-100"
                      >
                        <span className="educate-btn__curve" />
                        <FaPlay className="me-2" />
                        Join Using Desktop/Mobile
                      </button>
                    </div>
                  </>
                ) : (
                  <Button
                    variant="secondary"
                    disabled
                    className="d-flex align-items-center justify-content-center"
                  >
                    <FaPlay className="me-2" />
                    Coming Soon
                  </Button>
                ))}

              {/* {status.status === "completed" && (
                <button
                  // onClick={() =>
                  //   navigate("/recordings", { state: { class: cls } })
                  // }
                  className="educate-btn d-flex align-items-center justify-content-center mt-2"
                >
                  <FaPlay className="me-2" />
                  Watch Recording
                </button>
              )} */}

              {status.status === "completed" && cls.recording_url && (
                <button
                  onClick={() => window.open(cls.recording_url, "_blank")}
                  className="educate-btn w-100 mt-2 d-flex align-items-center justify-content-center"
                >
                  <span className="educate-btn__curve" />
                  <FaPlay className="me-2" />
                  Watch Recording
                </button>
              )}
            </div>
          </Accordion.Body>
        </Accordion.Item>
      );
    });
  };

  if (loading) {
    return (
      <Container className="py-5 text-center">
        <div className="page_header"></div>
        <Spinner animation="border" />
        <p>Loading live classes...</p>
      </Container>
    );
  }

  if (error) {
    return (
      <Container className="py-5 text-center">
        <div className="page_header"></div>
        <h3>Please purchase a plan for live class</h3>
        <Link
          to="/course-detail"
          onClick={() => localStorage.setItem("courseId", 74)}
          className="mt-3 educate-btn"
        >
          <span className="educate-btn__curve" />
          Purchase Plan
        </Link>
      </Container>
    );
  }

  return (
    <section className="live-class-section">
      <div className="page_header"></div>
      <Container>
        <Row>
          <Col xl={6} lg={6} md={7}>
            <h2 className="title mb-3 pt-3">Live Classes</h2>
            <div className="liveclassdesign">
              <Accordion
                activeKey={activeKey}
                onSelect={(key) => setActiveKey(key)}
              >
                {renderAccordionItems()}
              </Accordion>
            </div>
          </Col>

          <Col xl={6} lg={6} md={5} className="d-md-block d-none">
            <div className="imagelive">
              <img src={liveclass} alt="liveclass" />
            </div>
          </Col>
        </Row>

        {/* Toast container for messages */}
        <ToastContainer position="top-end" className="p-3">
          <Toast
            onClose={() => setShowToast(false)}
            show={showToast}
            delay={3000}
            autohide
            bg="danger"
          >
            <Toast.Header>
              <strong className="me-auto">Notice</strong>
            </Toast.Header>
            <Toast.Body className="text-white">{toastMessage}</Toast.Body>
          </Toast>
        </ToastContainer>
      </Container>

      {/* Model  */}

      <Modal
        show={show}
        onHide={handleClose}
        size="lg"
        centered
        className="live_intruction"
      >
        <Modal.Header closeButton>
          <Modal.Title>Install Zoom on Desktop / Mobile</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <h5>Windows</h5>
          <ol>
            <li>
              Go to the official Zoom download page:{" "}
              <a
                className="color-primary"
                href="https://zoom.us/download"
                target="_blank"
                rel="noreferrer"
              >
                https://zoom.us/download
              </a>
            </li>
            <li>
              Under Zoom Desktop Client, click <b>Download</b>.
            </li>
            <li>Open the downloaded file (ZoomInstaller.exe).</li>
            <li>Follow the installation prompts (Next → Install → Finish).</li>
            <li>
              Once installed, open Zoom and sign in (or join a meeting without
              signing in).
            </li>
          </ol>

          <h5>Mac (macOS)</h5>
          <ol>
            <li>
              Visit{" "}
              <a
                className="color-primary"
                href="https://zoom.us/download"
                target="_blank"
                rel="noreferrer"
              >
                https://zoom.us/download
              </a>
            </li>
            <li>
              Click <b>Download</b> under Zoom Desktop Client.
            </li>
            <li>Open the downloaded .pkg file.</li>
            <li>Follow the on-screen instructions to install.</li>
            <li>Open Zoom from Applications and sign in.</li>
          </ol>

          <h5>Install Zoom on Mobile</h5>

          {/* ✅ Show meeting_url if available */}
          {selectedClass?.meeting_url && (
            <p>
              Meeting Link:{" "}
              <a
                className="color-primary"
                href={selectedClass.meeting_url}
                target="_blank"
                rel="noreferrer"
              >
                {selectedClass.meeting_url}
              </a>
            </p>
          )}
          <h6>Android</h6>
          <ol>
            <li>Open Google Play Store.</li>
            <li>
              Search for <b>Zoom Cloud Meetings</b>.
            </li>
            <li>
              Tap <b>Install</b>.
            </li>
            <li>Open the app and sign in or join a meeting.</li>
          </ol>

          <h6>iPhone / iPad (iOS)</h6>
          <ol>
            <li>Open the App Store.</li>
            <li>
              Search for <b>Zoom Cloud Meetings</b>.
            </li>
            <li>
              Tap <b>Get → Install</b>.
            </li>
            <li>Open the app, then sign in or join a meeting.</li>
          </ol>

          <h5>After Installation</h5>
          <ul>
            <li>You can join meetings by clicking the Zoom meeting link.</li>
          </ul>
        </Modal.Body>
        <Modal.Footer>
          {selectedClass?.meeting_id && (
            <button
              onClick={() => {
                const meetingUrl = `zoommtg://zoom.us/join?action=join&confno=${selectedClass.meeting_id}&pwd=${selectedClass.password}`;
                window.location.href = meetingUrl;
              }}
              className="educate-btn"
            >
              <span className="educate-btn__curve" />
              Join Now in Zoom App
            </button>
          )}
          {/* {selectedClass?.meeting_url && (
            <Button
              variant="primary"
              onClick={() => window.open(selectedClass.meeting_url, "_blank")}
            >
              Join in Browser
            </Button>
          )} */}
          <button onClick={handleClose} className="educate-btn bg-primary">
            Close
          </button>
        </Modal.Footer>
      </Modal>
    </section>
  );
};

export default LiveClass;
